import { useState, useEffect } from 'react';
import { getMiDisponibilidad } from '../api';

/**
 * useIntervencionesDisponibilidad — participación del usuario en Citas Técnicas.
 *
 * Consulta qué entradas "personales" del módulo aplican a esta persona, en DOS
 * dimensiones (ver backend estadosParticipacion.js):
 *  - esUsuario/TecnicoActivo    → algo que hacer AHORA (campaña activa vigente).
 *                                 Gobierna lo que se ANUNCIA: rail, subsidebar,
 *                                 tarjeta del panel-inicio.
 *  - esUsuario/TecnicoHistorico → participó alguna vez (campaña no cancelada).
 *                                 AUTORIZA la entrada al histórico (el guard).
 *
 * El guard usa el HISTÓRICO (para no expulsar a quien tiene histórico que ver);
 * el subsidebar, el rail y la tarjeta usan el ACTIVO. Como el rail es
 * global, sin caché cada uno lanzaría su propia petición en cada navegación
 * (hasta 3 por carga). Por eso la petición se MEMOIZA a nivel de módulo:
 * una sola /mi-disponibilidad compartida por todos los consumidores.
 * La caché se invalida si cambia el token (re-login), si la petición falla
 * (para reintentar en el próximo montaje) o al vencer un TTL corto (5 min).
 * El TTL evita que, tras ser añadido a una campaña (aviso USUARIO_NUEVA_CAMPANA),
 * el usuario tenga que re-loguear para ver el módulo: aparece solo en <=5 min,
 * y en la práctica la petición sigue siendo única por navegación.
 *
 * Ante fallo (500 / corte de red) NO se esconde el módulo ni se muestra a
 * ciegas: se DEGRADA al eje de permisos (participación=false + `error=true` y
 * log). Un fallo transitorio no debe parecerse a "no tienes acceso": quien
 * tenga permiso fino sigue viendo el módulo; el eje de participación se reevalúa
 * al reintentar.
 *
 * Retorna { esUsuarioActivo, esTecnicoActivo, esUsuarioHistorico,
 * esTecnicoHistorico, loading, error }.
 * Durante `loading` devuelve todos `true` (anti-parpadeo: no esconder mientras
 * se resuelve). Los consumidores que no deben decidir en falso durante la carga
 * (p. ej. el guard) esperan a `loading === false`.
 */

// Caché compartida por todas las instancias: { token, promise, ts }.
let _cache = null;
// TTL corto: petición única por navegación pero refresco automático (p. ej. al
// ser añadido a una campaña) sin obligar a re-loguear.
const CACHE_TTL_MS = 5 * 60 * 1000;

function leerToken() {
  try { return localStorage.getItem('token'); } catch { return null; }
}

function cargarDisponibilidad() {
  const token = leerToken();
  const ahora = Date.now();
  if (_cache && _cache.token === token && (ahora - _cache.ts) < CACHE_TTL_MS) {
    return _cache.promise;
  }
  const promise = getMiDisponibilidad().catch((e) => {
    // Invalida la caché SOLO si sigue siendo la de esta carga, para permitir
    // reintento en el siguiente montaje sin pisar una carga posterior.
    if (_cache && _cache.token === token && _cache.ts === ahora) _cache = null;
    throw e;
  });
  _cache = { token, promise, ts: ahora };
  return promise;
}

export default function useIntervencionesDisponibilidad() {
  const [data, setData] = useState({
    esUsuarioActivo: true,
    esTecnicoActivo: true,
    esUsuarioHistorico: true,
    esTecnicoHistorico: true,
    loading: true,
    error: false,
  });

  useEffect(() => {
    let cancelado = false;

    cargarDisponibilidad()
      .then((res) => {
        if (cancelado) return;
        setData({
          esUsuarioActivo: !!res.es_usuario_activo,
          esTecnicoActivo: !!res.es_tecnico_activo,
          // ⚠ SHIM TEMPORAL — RETIRAR cuando AMBOS repos (este y el backend con
          // /mi-disponibilidad de 4 flags) estén en PRODUCCIÓN.
          // Compat de despliegue escalonado: si el backend aún no manda los
          // campos *_historico, el guard cae al valor *_activo anterior, que en
          // ese backend es el criterio permisivo (!= Cancelada) → no expulsa a
          // nadie. Es puramente para la ventana en la que el frontend nuevo
          // corre contra un backend viejo.
          // RETIRADA: sustituir cada rama por `!!res.es_usuario_historico` /
          // `!!res.es_tecnico_historico` a secas. Un shim permisivo que se queda
          // para siempre hace indepurable el próximo diagnóstico (en esta base ya
          // mordieron el PanelInicio muerto y la clave activo=0 con guard vivo).
          esUsuarioHistorico:
            res.es_usuario_historico !== undefined
              ? !!res.es_usuario_historico
              : !!res.es_usuario_activo,
          esTecnicoHistorico:
            res.es_tecnico_historico !== undefined
              ? !!res.es_tecnico_historico
              : !!res.es_tecnico_activo,
          loading: false,
          error: false,
        });
      })
      .catch((e) => {
        if (cancelado) return;
        // Degradación al eje de permisos: participación desconocida → false,
        // y se marca error para que el consumidor (o el log) lo distinga de un
        // "no participa" real.
        console.warn(
          '[useIntervencionesDisponibilidad] /mi-disponibilidad falló; se degrada al eje de permisos',
          e
        );
        setData({
          esUsuarioActivo: false,
          esTecnicoActivo: false,
          esUsuarioHistorico: false,
          esTecnicoHistorico: false,
          loading: false,
          error: true,
        });
      });

    return () => { cancelado = true; };
  }, []);

  return data;
}
